/**
 * Product / category configuration hooks.
 *
 * Configurations are configurable fields chosen when a product is referenced. A
 * configuration is owned by EXACTLY ONE scope — a product or a product category.
 * Category-owned configurations are inherited (read-only) by the category's
 * subcategories and by every product in the category / its subcategories, so the
 * list endpoints return the *effective* set (own + inherited) with each entry
 * flagged `is_inherited`.
 *
 * A configuration's `inventory_tracked` flag decides downstream behaviour:
 * stock-tracked (defines an inventory lot) vs post-pick (a line-item payload).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../../../shared/api/axiosInstance';
import { productKeys } from './useProducts';
import type { ConfigType, PriceAdjustmentType, ProductConfiguration, ProductConfigurationValue } from '../types';

const BASE = '/api/v1/master-data/products';

/** The owner scope a configuration mutation acts on. */
export type ConfigScope =
  | { type: 'product'; productId: string }
  | { type: 'category'; categoryId: string };

export interface ConfigurationValueInput {
  value: string;
  label?: string | null;
  price_adjustment_type?: PriceAdjustmentType;
  price_adjustment?: number | null;
  is_default?: boolean;
}

export interface ConfigurationInput {
  name: string;
  config_type: ConfigType;
  inventory_tracked?: boolean;
  is_required?: boolean;
  price_adjustment_type?: PriceAdjustmentType;
  price_adjustment?: number | null;
  values?: ConfigurationValueInput[];
}

export interface ConfigurationUpdateInput {
  name?: string;
  config_type?: ConfigType;
  inventory_tracked?: boolean;
  is_required?: boolean;
  price_adjustment_type?: PriceAdjustmentType;
  price_adjustment?: number | null;
  sort_order?: number;
}

export interface ConfigurationValueUpdateInput {
  value?: string;
  label?: string | null;
  price_adjustment_type?: PriceAdjustmentType;
  price_adjustment?: number | null;
  is_default?: boolean;
  sort_order?: number;
}

// ---------------------------------------------------------------- queries

export function useProductConfigurations(productId: string) {
  return useQuery({
    queryKey: productKeys.configurations(productId),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: ProductConfiguration[] }>(
        `${BASE}/products/${productId}/configurations`
      );
      return response.data.data;
    },
    enabled: !!productId,
  });
}

export function useCategoryConfigurations(categoryId: string) {
  return useQuery({
    queryKey: productKeys.categoryConfigurations(categoryId),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: ProductConfiguration[] }>(
        `${BASE}/categories/${categoryId}/configurations`
      );
      return response.data.data;
    },
    enabled: !!categoryId,
  });
}

// --------------------------------------------------------------- helpers

function useConfigurationInvalidator() {
  const queryClient = useQueryClient();
  return (scope: ConfigScope) => {
    if (scope.type === 'product') {
      queryClient.invalidateQueries({ queryKey: productKeys.configurations(scope.productId) });
      queryClient.invalidateQueries({ queryKey: productKeys.detail(scope.productId) });
    } else {
      // A category config change fans out to subcategories + every member
      // product's effective list — invalidate the whole products family
      // (which covers both product and category configuration queries).
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    }
  };
}

// -------------------------------------------------------------- mutations

export function useCreateConfiguration() {
  const invalidate = useConfigurationInvalidator();
  return useMutation({
    mutationFn: async ({ scope, data }: { scope: ConfigScope; data: ConfigurationInput }) => {
      const url =
        scope.type === 'product'
          ? `${BASE}/products/${scope.productId}/configurations`
          : `${BASE}/categories/${scope.categoryId}/configurations`;
      const response = await axiosInstance.post<{ data: ProductConfiguration }>(url, data);
      return response.data.data;
    },
    onSuccess: (_, { scope }) => invalidate(scope),
  });
}

export function useUpdateConfiguration() {
  const invalidate = useConfigurationInvalidator();
  return useMutation({
    mutationFn: async ({
      configurationId,
      data,
    }: {
      configurationId: string;
      scope: ConfigScope;
      data: ConfigurationUpdateInput;
    }) => {
      const response = await axiosInstance.put<{ data: ProductConfiguration }>(
        `${BASE}/configurations/${configurationId}`,
        data
      );
      return response.data.data;
    },
    onSuccess: (_, { scope }) => invalidate(scope),
  });
}

export function useDeleteConfiguration() {
  const invalidate = useConfigurationInvalidator();
  return useMutation({
    mutationFn: async ({ configurationId }: { configurationId: string; scope: ConfigScope }) => {
      await axiosInstance.delete(`${BASE}/configurations/${configurationId}`);
    },
    onSuccess: (_, { scope }) => invalidate(scope),
  });
}

export function useAddConfigurationValue() {
  const invalidate = useConfigurationInvalidator();
  return useMutation({
    mutationFn: async ({
      configurationId,
      data,
    }: {
      configurationId: string;
      scope: ConfigScope;
      data: ConfigurationValueInput;
    }) => {
      const response = await axiosInstance.post<{ data: ProductConfigurationValue }>(
        `${BASE}/configurations/${configurationId}/values`,
        data
      );
      return response.data.data;
    },
    onSuccess: (_, { scope }) => invalidate(scope),
  });
}

export function useUpdateConfigurationValue() {
  const invalidate = useConfigurationInvalidator();
  return useMutation({
    mutationFn: async ({
      valueId,
      data,
    }: {
      valueId: string;
      scope: ConfigScope;
      data: ConfigurationValueUpdateInput;
    }) => {
      const response = await axiosInstance.put<{ data: ProductConfigurationValue }>(
        `${BASE}/configuration-values/${valueId}`,
        data
      );
      return response.data.data;
    },
    onSuccess: (_, { scope }) => invalidate(scope),
  });
}

export function useDeleteConfigurationValue() {
  const invalidate = useConfigurationInvalidator();
  return useMutation({
    mutationFn: async ({ valueId }: { valueId: string; scope: ConfigScope }) => {
      await axiosInstance.delete(`${BASE}/configuration-values/${valueId}`);
    },
    onSuccess: (_, { scope }) => invalidate(scope),
  });
}
